import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

export type ConsoleNavEntry<K extends string = string> = { header: string } | { key: K; label: string; icon?: ReactNode };

export type ConsoleSidebarPalette = {
    background: string;
    text: string;
    muted: string;
    hover: string;
    activeText: string;
    accent: string;
    border?: string;
};

const SIDEBAR_WIDTH = 224;

const Dot = ({ color }: { color: string }) => (
    <Box
        component="span"
        sx={{ width: "6px", height: "6px", borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }}
    />
);

/** The full-height side menu shared by the full-bleed consoles (Tandem, Lens); each passes its own palette. */
const ConsoleSidebar = <K extends string>({
    brand,
    onBrandClick,
    items,
    active,
    onNavigate,
    palette,
    hiddenKeys,
}: {
    brand: ReactNode;
    onBrandClick?: () => void;
    items: ConsoleNavEntry<K>[];
    active: K;
    onNavigate: (key: K) => void;
    palette: ConsoleSidebarPalette;
    hiddenKeys?: Set<K>;
}) => {
    const navigate = useNavigate();

    // Drop hidden items, and drop a group header when its whole group is hidden.
    const visibleNav: ConsoleNavEntry<K>[] = [];
    let pendingHeader: ConsoleNavEntry<K> | null = null;
    items.forEach((entry) => {
        if ("header" in entry) {
            pendingHeader = entry;
            return;
        }
        if (hiddenKeys?.has(entry.key)) return;
        if (pendingHeader) {
            visibleNav.push(pendingHeader);
            pendingHeader = null;
        }
        visibleNav.push(entry);
    });

    return (
        <Box
            sx={{
                width: `${SIDEBAR_WIDTH}px`,
                flexShrink: 0,
                background: palette.background,
                color: palette.text,
                display: "flex",
                flexDirection: "column",
                padding: "20px 14px",
                ...(palette.border && { borderRight: `1px solid ${palette.border}` }),
            }}
        >
            <Box onClick={onBrandClick} sx={{ padding: "4px 10px 22px 10px", cursor: onBrandClick ? "pointer" : "default" }}>
                {brand}
            </Box>

            <Box component="nav" sx={{ display: "flex", flexDirection: "column", gap: "4px", minHeight: 0, overflowY: "auto" }}>
                {visibleNav.map((entry, i) => {
                    if ("header" in entry) {
                        return (
                            <Typography
                                key={`h-${i}`}
                                sx={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: palette.muted,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.09em",
                                    padding: "12px 10px 2px 10px",
                                }}
                            >
                                {entry.header}
                            </Typography>
                        );
                    }
                    const isActive = entry.key === active;
                    const marker = isActive ? palette.accent : palette.muted;
                    return (
                        <Box
                            key={entry.key}
                            onClick={() => onNavigate(entry.key)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "8px 10px",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "13.5px",
                                fontWeight: isActive ? 700 : 500,
                                color: isActive ? palette.activeText : palette.text,
                                background: isActive ? palette.hover : "transparent",
                                "&:hover": { background: palette.hover },
                            }}
                        >
                            {entry.icon ? (
                                <Box component="span" sx={{ display: "flex", color: marker, "& svg": { fontSize: "18px" } }}>
                                    {entry.icon}
                                </Box>
                            ) : (
                                <Dot color={marker} />
                            )}
                            <Box sx={{ flexGrow: 1 }}>{entry.label}</Box>
                        </Box>
                    );
                })}
            </Box>

            <Box
                onClick={() => navigate("/")}
                sx={{
                    marginTop: "auto",
                    fontSize: "12px",
                    color: palette.muted,
                    cursor: "pointer",
                    padding: "4px 10px",
                    "&:hover": { color: palette.activeText },
                }}
            >
                ← WorkNest Tools
            </Box>
        </Box>
    );
};

export default ConsoleSidebar;
